/**
 * DashboardNav - Top navigation bar for the dashboard
 */
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import StreakBadge from "@/components/StreakBadge";
import {
  Heart, User, Map, Plane, Binoculars, DollarSign, Book,
  Lightbulb, Sparkles, Users, History, Trophy, CalendarIcon,
  Download, MessageSquarePlus, BarChart3, Crown, Globe, Film
} from "lucide-react";
import { API } from "@/App";

export const DashboardNav = ({
  user,
  subscription,
  connected,
  isAdmin,
  onShowWorldMap,
  onShowTravelSearch,
  onShowExplore,
  onShowBudget,
  onShowScrapbook,
  onShowSuggestions,
  onShowMemories,
  onShowGroups,
  onShowPastTrips,
  onShowAchievements,
  onShowReminders,
  onShowExport,
  onShowYearInReview,
  onNotificationClick,
}) => {
  const navigate = useNavigate();

  // Determine the avatar URL - prioritize profile_picture_path
  const avatarUrl = user.profile_picture_path 
    ? `${API}/profile/picture/${user.profile_picture_path}`
    : user.preferences?.avatar_url || null;

  return (
    <nav className="sticky top-0 z-40 glass border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-16">
          <div className="flex items-center">
            <img src="/logo.png" alt="DuoLyst" className="w-16 h-10 sm:w-20 sm:h-12 lg:w-24 lg:h-14 object-contain" />
          </div>
          
          <div className="flex items-center gap-2 lg:gap-3">
            {user.partner_name && (
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/10">
                <Heart className="w-3.5 h-3.5 text-secondary" />
                <span className="text-sm font-medium text-foreground">with {user.partner_name}</span>
              </div>
            )}
            {connected && (
              <div className="hidden lg:flex items-center gap-1.5 px-2 py-1 rounded-full bg-secondary/10" title="Real-time updates active">
                <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                <span className="text-xs text-secondary font-medium">Live</span>
              </div>
            )}
            
            {/* Visit Streak Badge - visible on all screens */}
            <StreakBadge className="flex" />
            
            {/* Upgrade Button - only on large screens */}
            {(!subscription || subscription.plan_id === "free") && (
              <Button 
                onClick={() => navigate("/pricing")}
                className="hidden lg:flex bg-gradient-to-r from-primary to-primary/80 text-white hover:from-primary/90 hover:to-primary/70 rounded-full px-4 h-9 text-sm"
                data-testid="upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade
              </Button>
            )}
            
            {/* Nav Icons - only on large desktop screens (lg and up) */}
            <NavButton icon={Map} onClick={onShowWorldMap} title="World Map" testId="nav-map-btn" />
            <NavButton icon={Plane} onClick={onShowTravelSearch} title="Travel Search" testId="nav-travel-btn" />
            <NavButton icon={Binoculars} onClick={onShowExplore} title="Explore Destinations" testId="nav-explore-btn" />
            <NavButton icon={DollarSign} onClick={onShowBudget} title="Budget Tracker" testId="nav-budget-btn" />
            <NavButton icon={Book} onClick={onShowScrapbook} title="Scrapbook" testId="nav-scrapbook-btn" />
            <NavButton icon={Lightbulb} onClick={onShowSuggestions} title="Trip Suggestions" testId="nav-suggestions-btn" />
            <NavButton icon={Sparkles} onClick={onShowMemories} title="Memories Timeline" testId="nav-memories-btn" />
            <NavButton icon={Users} onClick={onShowGroups} title="Groups" testId="nav-groups-btn" />
            <NavButton icon={History} onClick={onShowPastTrips} title="Log Past Trips" testId="nav-past-trips-btn" />
            <NavButton icon={Trophy} onClick={onShowAchievements} title="Achievements" testId="nav-achievements-btn" iconClass="text-yellow-500" />
            <NavButton icon={CalendarIcon} onClick={onShowReminders} title="Reminders" testId="nav-reminders-btn" />
            <NavButton icon={Download} onClick={onShowExport} title="Export" testId="nav-export-btn" />
            <NavButton icon={Film} onClick={onShowYearInReview} title="Year in Review (MP4)" testId="nav-yir-btn" iconClass="text-pink-500" />
            
            <Link to="/feedback">
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full hidden sm:flex" 
                data-testid="nav-feedback-btn"
                title="Feedback"
              >
                <MessageSquarePlus className="w-5 h-5 text-foreground" />
              </Button>
            </Link>
            
            <Link to="/explore">
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-full hidden sm:flex" 
                data-testid="nav-community-btn"
                title="Explore Community"
              >
                <Globe className="w-5 h-5 text-foreground" />
              </Button>
            </Link>
            
            {isAdmin && (
              <Link to="/admin/analytics">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-full" 
                  data-testid="nav-analytics-btn"
                  title="Analytics (Admin)"
                >
                  <BarChart3 className="w-5 h-5 text-primary" />
                </Button>
              </Link>
            )}
            
            <NotificationBell onNotificationClick={onNotificationClick} />
            
            <ThemeToggle />
            
            <Link to="/profile">
              <Button variant="ghost" size="icon" className={`rounded-full overflow-hidden p-0 ${user.streak_frame_enabled ? 'streak-flame-frame' : ''}`} data-testid="nav-profile-btn">
                {avatarUrl ? (
                  <img 
                    src={avatarUrl} 
                    alt={user.name} 
                    className="w-9 h-9 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-9 h-9 rounded-full bg-primary/10 items-center justify-center ${avatarUrl ? 'hidden' : 'flex'}`}>
                  <User className="w-5 h-5 text-primary" />
                </div>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Helper component for nav buttons - only show on large screens (lg: 1024px+)
const NavButton = ({ icon: Icon, onClick, title, testId, iconClass = "text-foreground" }) => (
  <Button 
    variant="ghost" 
    size="icon" 
    className="rounded-full hidden lg:flex" 
    onClick={onClick}
    data-testid={testId}
    title={title}
  >
    <Icon className={`w-5 h-5 ${iconClass}`} />
  </Button>
);

export default DashboardNav;
